import { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Image, Platform } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { Users, Phone, Calendar, X, Check, Upload } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { Profile } from './types';
import { shadows } from '../../lib/styles';
import { pickFiles } from '../../lib/filePicker';
import {
  RESTRICTED_ADMIN_TABS,
  type RestrictedAdminTab,
  isSuperAdminEmail,
  normalizeAdminNavAccess,
} from './adminNavAccess';

const TeamTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<Profile[]>([]);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    full_name: string;
    phone_number: string;
    profile_photo_url: string;
  }>({
    full_name: '',
    phone_number: '',
    profile_photo_url: ''
  });
  const [savingMember, setSavingMember] = useState<string | null>(null);
  const [savingNavAccessMember, setSavingNavAccessMember] = useState<string | null>(null);
  const [savingWalkthroughHostMember, setSavingWalkthroughHostMember] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsSuperAdmin(isSuperAdminEmail(user?.email));
    };

    fetchCurrentUser();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          phone_number,
          created_at,
          plan,
          is_system_manager,
          home_manager,
          profile_photo_url,
          admin_nav_access,
          is_walkthrough_host
        `)
        .eq('is_system_manager', true)
        .order('full_name', { ascending: true });

      if (profilesError) throw profilesError;

      setTeamMembers(profiles || []);
    } catch (error: any) {
      console.error('Error fetching team members:', error);
      setError('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMember = (member: Profile) => {
    setEditingMember(member.id);
    setEditForm({
      full_name: member.full_name || '',
      phone_number: member.phone_number || '',
      profile_photo_url: member.profile_photo_url || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setEditForm({
      full_name: '',
      phone_number: '',
      profile_photo_url: ''
    });
  };

  const handlePhotoUpload = async (memberId: string, file: File) => {
    try {
      setUploadingPhoto(memberId);
      setError(null);

      // Generate a unique file name with timestamp
      const fileExt = file.name.split('.').pop();
      const fileName = `${memberId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('profile-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(filePath);

      // Update the form with the new URL
      setEditForm(prev => ({
        ...prev,
        profile_photo_url: publicUrl
      }));

      setSuccess('Photo uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      setError(`Failed to upload photo: ${error.message}`);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePhotoSelect = async (memberId: string) => {
    if (Platform.OS === 'web') {
      // On web, trigger the hidden file input
      fileInputRef.current?.click();
    } else {
      // On mobile, use the file picker
      const files = await pickFiles();
      if (files.length > 0) {
        await handlePhotoUpload(memberId, files[0]);
      }
    }
  };

  const handleFileInputChange = async (event: any, memberId: string) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      await handlePhotoUpload(memberId, file);
    }
  };

  const handleSaveMember = async (memberId: string) => {
    try {
      setSavingMember(memberId);
      setError(null);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: editForm.full_name,
          phone_number: editForm.phone_number,
          profile_photo_url: editForm.profile_photo_url || null
        })
        .eq('id', memberId);

      if (updateError) throw updateError;

      // Update local state
      setTeamMembers(prevMembers =>
        prevMembers.map(m =>
          m.id === memberId
            ? {
                ...m,
                full_name: editForm.full_name,
                phone_number: editForm.phone_number,
                profile_photo_url: editForm.profile_photo_url || undefined
              }
            : m
        )
      );

      setSuccess('Team member updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      setEditingMember(null);
    } catch (error: any) {
      console.error('Error updating team member:', error);
      setError(`Failed to update team member: ${error.message}`);
    } finally {
      setSavingMember(null);
    }
  };

  const restrictedTabLabels: Record<RestrictedAdminTab, string> = {
    team: 'Team',
    ai: 'AI Assistant',
    debug: 'Debug Tools',
    announcements: 'Announcements',
    reports: 'Reports',
    quotes: 'Market Research Quotes',
  };

  const handleToggleWalkthroughHost = async (member: Profile, enabled: boolean) => {
    try {
      setSavingWalkthroughHostMember(member.id);
      setError(null);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ is_walkthrough_host: enabled })
        .eq('id', member.id);

      if (updateError) throw updateError;

      setTeamMembers((prevMembers) =>
        prevMembers.map((m) =>
          m.id === member.id ? { ...m, is_walkthrough_host: enabled } : m
        )
      );
    } catch (toggleError: any) {
      console.error('Error updating walkthrough host flag:', toggleError);
      setError(`Failed to update walkthrough availability: ${toggleError.message}`);
    } finally {
      setSavingWalkthroughHostMember(null);
    }
  };

  const handleToggleNavAccess = async (member: Profile, tab: RestrictedAdminTab) => {
    try {
      setSavingNavAccessMember(member.id);
      setError(null);

      const currentAccess = normalizeAdminNavAccess(member.admin_nav_access);
      const nextAccess = {
        ...currentAccess,
        [tab]: !(currentAccess[tab] === true),
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ admin_nav_access: nextAccess })
        .eq('id', member.id);

      if (updateError) throw updateError;

      setTeamMembers((prevMembers) =>
        prevMembers.map((m) =>
          m.id === member.id
            ? {
                ...m,
                admin_nav_access: nextAccess,
              }
            : m
        )
      );
    } catch (toggleError: any) {
      console.error('Error updating navigation access:', toggleError);
      setError(`Failed to update navigation access: ${toggleError.message}`);
    } finally {
      setSavingNavAccessMember(null);
    }
  };

  const renderTeamMemberCard = (member: Profile) => {
    const isEditing = editingMember === member.id;
    const isSaving = savingMember === member.id;

    const navAccess = normalizeAdminNavAccess(member.admin_nav_access);
    const isSavingNavAccess = savingNavAccessMember === member.id;
    const isSavingWalkthroughHost = savingWalkthroughHostMember === member.id;
    const walkthroughHostEnabled = member.is_walkthrough_host === true;

    return (
      <View key={member.id} style={styles.memberCard}>
        <View style={styles.memberHeader}>
          <View style={styles.memberPhotoSection}>
            {(isEditing ? editForm.profile_photo_url : member.profile_photo_url) ? (
              <Image
                source={{ uri: isEditing ? editForm.profile_photo_url : member.profile_photo_url }}
                style={styles.memberPhoto}
              />
            ) : (
              <View style={styles.memberPhotoPlaceholder}>
                <Users size={32} color="#9ca3af" />
              </View>
            )}
            {isEditing && (
              <Pressable
                onPress={() => handlePhotoSelect(member.id)}
                disabled={uploadingPhoto === member.id}
                style={({ pressed }) => [
                  styles.uploadPhotoButton,
                  pressed && styles.uploadPhotoButtonPressed,
                  uploadingPhoto === member.id && styles.uploadPhotoButtonDisabled
                ]}
              >
                {uploadingPhoto === member.id ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <>
                    <Upload size={14} color="#ffffff" />
                    <Text style={styles.uploadPhotoButtonText}>Upload</Text>
                  </>
                )}
              </Pressable>
            )}
          </View>

          <View style={styles.memberInfo}>
            {isEditing ? (
              <View style={styles.editForm}>
                <View style={styles.editFormGroup}>
                  <Text style={styles.editLabel}>Full Name</Text>
                  <TextInput
                    style={styles.editInput}
                    value={editForm.full_name}
                    onChangeText={(value) => setEditForm({ ...editForm, full_name: value })}
                    placeholder="Enter full name"
                  />
                </View>
                <View style={styles.editFormGroup}>
                  <Text style={styles.editLabel}>Phone Number</Text>
                  <TextInput
                    style={styles.editInput}
                    value={editForm.phone_number}
                    onChangeText={(value) => setEditForm({ ...editForm, phone_number: value })}
                    placeholder="(555) 123-4567"
                    keyboardType="phone-pad"
                  />
                </View>
                <View style={styles.editFormGroup}>
                  <Text style={styles.editLabel}>Profile Photo URL</Text>
                  <TextInput
                    style={styles.editInput}
                    value={editForm.profile_photo_url}
                    onChangeText={(value) => setEditForm({ ...editForm, profile_photo_url: value })}
                    placeholder="https://example.com/photo.jpg"
                    autoCapitalize="none"
                  />
                </View>
              </View>
            ) : (
              <>
                <Text style={styles.memberName}>{member.full_name}</Text>
                <View style={styles.memberDetails}>
                  {member.phone_number && (
                    <View style={styles.memberDetail}>
                      <Phone size={14} color="#6b7280" />
                      <Text style={styles.memberDetailText}>{member.phone_number}</Text>
                    </View>
                  )}
                  <View style={styles.memberDetail}>
                    <Calendar size={14} color="#6b7280" />
                    <Text style={styles.memberDetailText}>
                      Joined {new Date(member.created_at).toLocaleDateString()}
                    </Text>
                  </View>
                </View>
              </>
            )}
          </View>

          <View style={styles.memberActions}>
            {isEditing ? (
              <>
                <Pressable
                  onPress={() => handleSaveMember(member.id)}
                  disabled={isSaving}
                  style={({ pressed }) => [
                    styles.actionButton,
                    styles.saveButton,
                    pressed && styles.actionButtonPressed,
                    isSaving && styles.actionButtonDisabled
                  ]}
                >
                  {isSaving ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <>
                      <Check size={16} color="#ffffff" />
                      <Text style={styles.saveButtonText}>Save</Text>
                    </>
                  )}
                </Pressable>
                <Pressable
                  onPress={handleCancelEdit}
                  disabled={isSaving}
                  style={({ pressed }) => [
                    styles.actionButton,
                    styles.cancelButton,
                    pressed && styles.actionButtonPressed,
                    isSaving && styles.actionButtonDisabled
                  ]}
                >
                  <X size={16} color="#4b5563" />
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
              </>
            ) : (
              <Pressable
                onPress={() => handleEditMember(member)}
                style={({ pressed }) => [
                  styles.actionButton,
                  styles.editButton,
                  pressed && styles.actionButtonPressed
                ]}
              >
                <Text style={styles.editButtonText}>Edit</Text>
              </Pressable>
            )}
          </View>
        </View>

        {member.home_manager && (
          <View style={styles.memberFooter}>
            <View style={styles.roleBadge}>
              <Text style={styles.roleBadgeText}>
                Home Manager: {member.home_manager}
              </Text>
            </View>
          </View>
        )}

        {isSuperAdmin && (
          <View style={styles.navAccessSection}>
            <Text style={styles.navAccessSectionTitle}>Walkthrough availability</Text>
            <Text style={styles.navAccessHelpText}>
              Team members enabled here appear when scheduling walkthrough dates (Walkthrough Availability tab).
            </Text>
            <Pressable
              onPress={() => handleToggleWalkthroughHost(member, !walkthroughHostEnabled)}
              disabled={isSavingWalkthroughHost}
              style={({ pressed }) => [
                styles.navAccessItem,
                walkthroughHostEnabled && styles.navAccessItemEnabled,
                pressed && styles.actionButtonPressed,
                isSavingWalkthroughHost && styles.actionButtonDisabled,
              ]}
            >
              <Text
                style={[styles.navAccessItemLabel, walkthroughHostEnabled && styles.navAccessItemLabelEnabled]}
              >
                Listed for walkthrough scheduling
              </Text>
              <Text
                style={[styles.navAccessItemValue, walkthroughHostEnabled && styles.navAccessItemValueEnabled]}
              >
                {isSavingWalkthroughHost ? '…' : walkthroughHostEnabled ? 'Yes' : 'No'}
              </Text>
            </Pressable>
          </View>
        )}

        {isSuperAdmin && (
          <View style={styles.navAccessSection}>
            <Text style={styles.navAccessSectionTitle}>Admin Navigation Access</Text>
            <Text style={styles.navAccessHelpText}>
              Toggle which restricted admin tabs this team member can access.
            </Text>
            <View style={styles.navAccessList}>
              {RESTRICTED_ADMIN_TABS.map((tab) => {
                const enabled = navAccess[tab] === true;
                return (
                  <Pressable
                    key={`${member.id}-${tab}`}
                    onPress={() => handleToggleNavAccess(member, tab)}
                    disabled={isSavingNavAccess}
                    style={({ pressed }) => [
                      styles.navAccessItem,
                      enabled && styles.navAccessItemEnabled,
                      pressed && styles.actionButtonPressed,
                      isSavingNavAccess && styles.actionButtonDisabled,
                    ]}
                  >
                    <Text style={[styles.navAccessItemLabel, enabled && styles.navAccessItemLabelEnabled]}>
                      {restrictedTabLabels[tab]}
                    </Text>
                    <Text style={[styles.navAccessItemValue, enabled && styles.navAccessItemValueEnabled]}>
                      {enabled ? 'Enabled' : 'Disabled'}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        )}
      </View>
    );
  };

  if (loading && teamMembers.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
        <Text style={styles.loadingText}>Loading team members...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Hidden file input for web */}
      {Platform.OS === 'web' && (
        <input
          ref={fileInputRef as any}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const currentEditingId = editingMember;
            if (currentEditingId) {
              handleFileInputChange(e, currentEditingId);
            }
          }}
        />
      )}

      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Users size={24} color="#000000" />
          <Text style={styles.title}>Team Members</Text>
        </View>
        <Pressable
          style={styles.refreshButton}
          onPress={() => fetchTeamMembers()}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#4b5563" />
          ) : (
            <Text style={styles.refreshButtonText}>Refresh</Text>
          )}
        </Pressable>
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {success && (
        <View style={styles.successContainer}>
          <Text style={styles.successText}>{success}</Text>
        </View>
      )}

      {teamMembers.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Users size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No Team Members Found</Text>
          <Text style={styles.emptyText}>There are no team members in the system yet.</Text>
        </View>
      ) : (
        <WebScrollView>
          <View style={styles.membersList}>
            {teamMembers.map(member => renderTeamMemberCard(member))}
          </View>
        </WebScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: '#000000',
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#4b5563',
    fontWeight: '500',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    color: '#6b7280',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 48,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#000000',
  },
  emptyText: {
    fontSize: 14,
    color: '#6b7280',
  },
  errorContainer: {
    backgroundColor: '#fee2e2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  successContainer: {
    backgroundColor: '#d1fae5',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  successText: {
    color: '#065f46',
    fontSize: 14,
  },
  membersList: {
    gap: 16,
  },
  memberCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 20,
    ...shadows.sm,
  },
  memberHeader: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  memberPhotoSection: {
    width: 80,
    height: 80,
    position: 'relative',
  },
  memberPhoto: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#f3f4f6',
  },
  memberPhotoPlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadPhotoButton: {
    position: 'absolute',
    bottom: -8,
    right: -8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#000000',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#ffffff',
    ...shadows.sm,
  },
  uploadPhotoButtonPressed: {
    opacity: 0.7,
  },
  uploadPhotoButtonDisabled: {
    opacity: 0.5,
  },
  uploadPhotoButtonText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#ffffff',
  },
  memberInfo: {
    flex: 1,
    gap: 8,
  },
  memberName: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  memberDetails: {
    gap: 6,
  },
  memberDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  memberDetailText: {
    fontSize: 14,
    color: '#6b7280',
  },
  memberActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    minHeight: 36,
  },
  actionButtonPressed: {
    opacity: 0.7,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  editButton: {
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  editButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4b5563',
  },
  saveButton: {
    backgroundColor: '#000000',
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  cancelButton: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4b5563',
  },
  memberFooter: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  roleBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#dbeafe',
  },
  roleBadgeText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#1e40af',
  },
  editForm: {
    gap: 12,
  },
  editFormGroup: {
    gap: 4,
  },
  editLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  editInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  navAccessSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 8,
  },
  navAccessSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  navAccessHelpText: {
    fontSize: 12,
    color: '#6b7280',
  },
  navAccessList: {
    gap: 8,
  },
  navAccessItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  navAccessItemEnabled: {
    borderColor: '#86efac',
    backgroundColor: '#f0fdf4',
  },
  navAccessItemLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  navAccessItemLabelEnabled: {
    color: '#166534',
  },
  navAccessItemValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  navAccessItemValueEnabled: {
    color: '#166534',
  },
});

export default TeamTab;

